import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { useApiPagination, useDebounce } from "sj-ui-toolkit/src/hooks";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import type { AssignmentReportRow, AssignmentsReportResponse } from "@/api/assignmentsReport";
import { loadAssignmentsReportPage, toApiSortField } from "@/api/assignmentsReport";
import {
  ASSIGNMENT_STATUS_CONFIG,
  getAssignmentStatusKey,
} from "@/constants/assignmentsReport.constants";
import { withSkeleton, statusBody, dateBody } from "@/helpers/tableBody";

// ---------------------------------------------------------------------------
// Column body templates (module-level — no runtime deps)
// ---------------------------------------------------------------------------

const emailBody = withSkeleton<AssignmentReportRow>(row => row.email, "14rem");
const firstNameBody = withSkeleton<AssignmentReportRow>(row => row.firstName ?? "--", "6rem");
const lastNameBody = withSkeleton<AssignmentReportRow>(row => row.lastName ?? "--", "6rem");
const timeSpentBody = withSkeleton<AssignmentReportRow>(row => row.timeSpent ?? "\u2014", "5rem");
const dueDateBody = withSkeleton<AssignmentReportRow>(
  row => humanDateWithoutTime(row.dueDate),
  "5rem"
);
const completedAtBody = dateBody<AssignmentReportRow>("completedAt", "Incomplete");
const assignmentStatusBody = statusBody<AssignmentReportRow>(row => {
  const key = getAssignmentStatusKey(row);
  const config = ASSIGNMENT_STATUS_CONFIG[key];
  return {
    icon: config.icon || undefined,
    colorClass: config.color ? `text-${config.color}` : undefined,
    label: row.status ?? "\u2014",
  };
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface AssignmentUsersTableProps {
  assignmentName: string;
}

/** Assigned users + completion results for a single assignment, scoped by name (report API has no id filter). */
export const AssignmentUsersTable = ({ assignmentName }: AssignmentUsersTableProps) => {
  const hideTimeSpent = useHideTimeSpent();

  const { data, totalRecords, lazyState, onPage, onSort, onFilter } = useApiPagination({
    storageKey: `assignmentDetailsUsers-${assignmentName}`,
    dataKey: "assignments",
    calculateTotalRecords: (res: AssignmentsReportResponse, pageSize: number) => {
      if (!res?.assignments) return 0;
      const total = res.totalPages ?? 0;
      if (total <= 1) return res.assignments.length;
      return (total - 1) * pageSize + res.assignments.length;
    },
    loadData: async ({ page, perPage, sortField, sortOrder, filters }) => {
      const global = (filters as { global?: { value?: string | null } } | undefined)?.global?.value;
      return await loadAssignmentsReportPage({
        page,
        per_page: perPage,
        sortable_column: toApiSortField(sortField),
        order_type: sortOrder === 1 ? "asc" : "desc",
        query: global ?? undefined,
        assignment: [assignmentName],
      });
    },
  });

  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(searchText, 300);
  const searchMounted = useRef(false);

  useEffect(() => {
    if (!searchMounted.current) {
      searchMounted.current = true;
      return;
    }
    onFilter({ ...lazyState, filters: { global: { value: debouncedSearch || null } } });
  }, [debouncedSearch]);

  return (
    <div>
      <IconField iconPosition="left" className="mb-4 max-w-72">
        <InputIcon className="pi pi-search" />
        <InputText
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          placeholder="Search users..."
          aria-label="Search assigned users"
          data-testid="assignment-details-users-search-input"
          className="w-full"
        />
      </IconField>
      <DataTable
        value={data}
        lazy
        paginator
        data-testid="assignment-details-users-table"
        rows={lazyState.rows}
        first={lazyState.first}
        totalRecords={totalRecords}
        sortField={lazyState.sortField ?? undefined}
        sortOrder={lazyState.sortOrder as 1 | -1 | 0 | null | undefined}
        onPage={e => void onPage(e)}
        onSort={onSort}
        removableSort
        rowsPerPageOptions={[10, 25, 50, 100]}
        emptyMessage="No users assigned yet."
        tableClassName="dt-responsive-text"
      >
        <Column field="email" header="Email" sortable style={{ width: "300px" }} body={emailBody} />
        <Column field="firstName" header="First" sortable body={firstNameBody} />
        <Column field="lastName" header="Last" sortable body={lastNameBody} />
        {!hideTimeSpent && (
          <Column
            field="timeSpent"
            header="Time Spent"
            style={{ width: "150px" }}
            body={timeSpentBody}
          />
        )}
        <Column
          field="dueDate"
          header="Due Date"
          sortable
          style={{ width: "150px" }}
          body={dueDateBody}
        />
        <Column
          field="completedAt"
          header="Completion Date"
          style={{ width: "200px" }}
          body={completedAtBody}
        />
        <Column field="status" header="Status" sortable body={assignmentStatusBody} />
      </DataTable>
    </div>
  );
};
